export default function NHLGamePanel({ game }) {
  const away = game.awayTeam || {};
  const home = game.homeTeam || {};
  const teamStats = game.teamStats || [];
  const awayStats = (teamStats.find(t => t.teamId === away.id) || {}).stats || {};
  const homeStats = (teamStats.find(t => t.teamId === home.id) || {}).stats || {};

  const statRows = [
    { label: "Shots", away: awayStats["shotsTotal"], home: homeStats["shotsTotal"] },
    { label: "Hits", away: awayStats["hits"], home: homeStats["hits"] },
    { label: "Blocked Shots", away: awayStats["blockedShots"], home: homeStats["blockedShots"] },
    { label: "Takeaways", away: awayStats["takeaways"], home: homeStats["takeaways"] },
    { label: "PP Goals", away: awayStats["powerPlayGoals"], home: homeStats["powerPlayGoals"] },
    { label: "PP Opportunities", away: awayStats["powerPlayOpportunities"], home: homeStats["powerPlayOpportunities"] },
    { label: "Faceoff %", away: awayStats["faceoffWinPct"], home: homeStats["faceoffWinPct"] },
  ].filter(r => r.away || r.home);

  const awayPP = awayStats["powerPlayGoals"] && awayStats["powerPlayOpportunities"];
  const homePP = homeStats["powerPlayGoals"] && homeStats["powerPlayOpportunities"];

  return (
    <div className="space-y-4" data-testid="nhl-panel">
      {/* Period-by-period scoring */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 w-44">Team</th>
                {(home.linescores || away.linescores || []).map((_, i) => (
                  <th key={i} className="text-center px-3 py-2.5 w-10">{i < 3 ? `P${i + 1}` : i === 3 ? "OT" : "SO"}</th>
                ))}
                <th className="text-center px-3 py-2.5 w-12 bg-white/5 font-bold">T</th>
              </tr>
            </thead>
            <tbody>
              {[away, home].map((team) => (
                <tr key={team.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {team.logo && <img src={team.logo} alt="" className="w-5 h-5 object-contain" />}
                      <span className="font-semibold text-white">{team.displayName || team.name}</span>
                    </div>
                  </td>
                  {(team.linescores || []).map((s, i) => (
                    <td key={i} className="text-center px-3 py-2.5 score-font text-white/60">{s}</td>
                  ))}
                  <td className="text-center px-3 py-2.5 score-font font-bold text-white bg-white/5">{team.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Power Play + Shots comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Shots on Goal visual */}
        {(awayStats["shotsTotal"] || homeStats["shotsTotal"]) && (
          <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="shots-comparison">
            <h3 className="text-[10px] tracking-widest uppercase text-white/40 mb-3">Shots on Goal</h3>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {away.logo && <img src={away.logo} alt="" className="w-6 h-6 object-contain" />}
                <span className="score-font text-2xl text-white font-bold">{awayStats["shotsTotal"] || "0"}</span>
              </div>
              <span className="text-xs text-white/30">SOG</span>
              <div className="flex items-center gap-2">
                <span className="score-font text-2xl text-white font-bold">{homeStats["shotsTotal"] || "0"}</span>
                {home.logo && <img src={home.logo} alt="" className="w-6 h-6 object-contain" />}
              </div>
            </div>
            <div className="flex h-2 rounded-full overflow-hidden bg-white/10">
              <div className="h-full transition-all" style={{
                width: `${(parseFloat(awayStats["shotsTotal"] || 0) / Math.max(parseFloat(awayStats["shotsTotal"] || 0) + parseFloat(homeStats["shotsTotal"] || 0), 1)) * 100}%`,
                backgroundColor: away.color ? `#${away.color}` : "#007AFF",
              }} />
              <div className="h-full transition-all" style={{
                width: `${(parseFloat(homeStats["shotsTotal"] || 0) / Math.max(parseFloat(awayStats["shotsTotal"] || 0) + parseFloat(homeStats["shotsTotal"] || 0), 1)) * 100}%`,
                backgroundColor: home.color ? `#${home.color}` : "#FF3B30",
              }} />
            </div>
          </div>
        )}

        {/* Power Play */}
        {(awayPP || homePP) && (
          <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="power-play">
            <h3 className="text-[10px] tracking-widest uppercase text-white/40 mb-3">Power Play</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/60">{away.abbreviation}</span>
                <span className="score-font text-sm text-white">{awayStats["powerPlayGoals"] || "0"}/{awayStats["powerPlayOpportunities"] || "0"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/60">{home.abbreviation}</span>
                <span className="score-font text-sm text-white">{homeStats["powerPlayGoals"] || "0"}/{homeStats["powerPlayOpportunities"] || "0"}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full stats table */}
      {statRows.length > 0 && (
        <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="nhl-stats">
          <div className="px-4 py-2.5 border-b border-white/5">
            <h3 className="text-[10px] tracking-widest uppercase font-bold text-white/40">Team Stats</h3>
          </div>
          <div className="divide-y divide-white/5">
            {statRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between px-4 py-2 text-xs">
                <span className="score-font text-white/70 w-16 text-right">{row.away || "0"}</span>
                <span className="text-white/40 uppercase tracking-wider text-[10px] flex-1 text-center">{row.label}</span>
                <span className="score-font text-white/70 w-16">{row.home || "0"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
